import API from '../Services/API';
import { toQueryString } from '../Services/helpers';
import Model from './Model';
import State from './State';

/*
 * Accounts
 */

const useExpenseAccounts = (alwaysRefresh = false) => {
    return Model({
        alwaysRefresh: alwaysRefresh,
        returnDataKey: 'accounts',
        stateKey: 'expenseAccounts',
        fetchData: () => API.get('/expenses/accounts'),
        registerAdditionalMethods: (opts) => {
            return {
                openAccount: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/expenses/accounts/', data),
                        'Expense account opened',
                        true,
                    );
                },
                updateAccount: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/expenses/accounts/'+data.id, data),
                        'Expense account updated',
                        true,
                    );
                },
                deleteAccount: (accountId) => {
                    return opts.wrapUpdateCall(
                        () => API.delete('/expenses/accounts/'+accountId, null),
                        'Expense account closed',
                        true,
                    );
                },
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading expense accounts.',
    });
}

/*
 * Categories
 */

const useExpenseCategories = (alwaysRefresh = false) => {
    return Model({
        alwaysRefresh: alwaysRefresh,
        returnDataKey: 'categories',
        stateKey: 'expenseCategories',
        fetchData: () => API.get('/expenses/categories'),
        registerAdditionalMethods: (opts) => {
            return {
                addCategory: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/expenses/categories/', data),
                        'Expense category added',
                        true,
                    );
                },
                updateCategory: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/expenses/categories/'+data.id, data),
                        'Expense category updated',
                        true,
                    );
                },
                deleteCategory: (categoryId) => {
                    return opts.wrapUpdateCall(
                        () => API.delete('/expenses/categories/'+categoryId, null),
                        'Expense category deleted',
                        true,
                    );
                },
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading expense categories.',
    });
}

/*
 * Expenses
 */

const useExpenses = (searchData, alwaysRefresh = false) => {
    const searchStr = toQueryString(searchData);

    return Model({
        alwaysRefresh: alwaysRefresh,
        returnDataKey: 'expenses',
        stateKey: ['expenses', searchStr],
        fetchData: () => API.get(`/expenses?${searchStr}`),
        setAdditionalData: (expenses) => {
            if (expenses && expenses?.length) {
                expenses.forEach(expense => {
                    State.putIfNotExists(['expense', expense.id], expense)
                })
            }
        },
        registerAdditionalMethods: (opts) => {
            return {
                /*
                addExpense: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/expenses/${accountId}/expenses/`, data),
                        'Expense added',
                        true,
                    );
                },
                updateExpense: (expenseId, data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/expenses/${accountId}/expenses/${expenseId}`, data),
                        'Expense updated',
                        true,
                    );
                },
                */
                deleteExpense: (expense) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/expenses/${expense.accountId}/expenses/${expense.id}`, null),
                        'Expense deleted',
                        true,
                    );
                },
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading expenses.',
    })
}

const useExpense = (expenseId = null, alwaysRefresh = false) => {
    return Model({
        alwaysRefresh: alwaysRefresh,
        returnDataKey: 'expense',
        stateKey: ['expense', expenseId],
        block: !expenseId,
        fetchData: () => API.get(`/expenses/${expenseId}`),
        registerAdditionalMethods: (opts) => {
            const model = opts.model()
            return {
                updateExpense: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/expenses/${model.accountId}/expenses/${model.id}`, data),
                        'Expense updated',
                        true,
                    );
                },
                /*
                deleteExpense: () => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/expenses/${model.accountId}/expenses/${model.id}`, null),
                        'Expense deleted',
                        true,
                    );
                },
                */
            }
        },
        defaultLoadingError: 'Sorry, there was an error loading the expense.',
    })
}

const importExpenseFromAsset = ({accountId, expenseId, assetId}) => {
    return new Promise((resolve, reject) => {
        const data = {
            expenseId,
            assetId,
        };
        API.post(`/expenses/${accountId}/expenses/import/`, data).then(res => {
            resolve(res);
        }).catch(err => {
            reject(err);
        });
    });
}

const addExpense = (accountId, data) => {
    return new Promise((resolve, reject) => {
        API.post(`/expenses/${accountId}/expenses`, data).then(res => {
            resolve(res);
        }).catch(err => {
            reject(err);
        });
    });
}

const useStats = (dateRange, alwaysRefresh = false) => {
    return Model({
        alwaysRefresh: alwaysRefresh,
        returnDataKey: 'stats',
        stateKey: ['expense', 'stats', dateRange],
        fetchData: () => API.get('/expenses/stats?dateRange=' + dateRange),
        defaultLoadingError: 'Sorry, there was an error loading expense stats.',
    });
}

/*
 * Export
 */
const exportExpenses = (dateRange) => {
    return API.get('/expenses/export?dateRange=' + dateRange);
}

export {
    useExpenseAccounts,
    useExpenseCategories,
    importExpenseFromAsset,
    addExpense,
    useExpenses,
    useExpense,
    exportExpenses,
    useStats,
}
